import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { transportProviderAPI } from '../../services/api';
import { TRANSPORT_ORDER_STATUS_LABELS as STATUS_LABELS } from './TransportProviderOrdersPage';

const EVENT_LABELS = {
  created: 'تم إنشاء الطلب', payment_submitted: 'تم رفع إيصال الدفع', payment_confirmed: 'تم تأكيد الدفع',
  vehicle_assigned: 'تم تعيين المركبة والسائق', picked_up: 'تم استلام البضاعة', in_transit: 'في الطريق',
  delivered: 'تم التسليم',
};

const inputCls = 'w-full border-2 border-gray-200 hover:border-blue-400/40 focus:border-blue-600 focus:ring-4 focus:ring-blue-100 rounded-xl px-4 py-2.5 text-sm transition-all outline-none';
const labelCls = 'block text-xs font-bold text-gray-600 mb-1.5';

const TransportProviderOrderDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [vehicles, setVehicles] = useState([]);
  const [acting, setActing]   = useState(false);

  // Assign-vehicle form state
  const [vehicleId, setVehicleId]     = useState('');
  const [pickupOtpInput, setPickupOtpInput]     = useState('');
  const [deliveryOtpInput, setDeliveryOtpInput] = useState('');

  // silent=true for background polls — only the very first load should show
  // the full-page spinner.
  const load = useCallback((silent = false) => {
    if (!silent) setLoading(true);
    transportProviderAPI.getOrderById(id)
      .then((res) => setOrder(res.data.data))
      .catch(() => toast.error('تعذّر تحميل الطلب'))
      .finally(() => { if (!silent) setLoading(false); });
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    let interval = setInterval(() => load(true), 15000);
    const handleVisibility = () => {
      if (document.hidden) {
        clearInterval(interval);
      } else {
        load(true);
        interval = setInterval(() => load(true), 15000);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [load]);
  useEffect(() => {
    transportProviderAPI.getVehicles().then((res) => setVehicles(res.data.data || [])).catch(() => {});
  }, []);

  const handleAssign = async (e) => {
    e.preventDefault();
    if (!vehicleId) { toast.error('يرجى اختيار مركبة'); return; }
    setActing(true);
    try {
      await transportProviderAPI.assignVehicle(id, { vehicleId });
      toast.success('تم تعيين المركبة — تم إرسال رمز الاستلام للعميل');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'تعذّر تعيين المركبة');
    } finally { setActing(false); }
  };

  const handleConfirmPickup = async (e) => {
    e.preventDefault();
    setActing(true);
    try {
      await transportProviderAPI.confirmPickup(id, pickupOtpInput.trim());
      toast.success('تم تأكيد الاستلام');
      setPickupOtpInput('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'رمز غير صحيح');
    } finally { setActing(false); }
  };

  const handleMarkInTransit = async () => {
    setActing(true);
    try {
      await transportProviderAPI.markInTransit(id);
      toast.success('تم التحديث إلى في الطريق');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'تعذّر التحديث');
    } finally { setActing(false); }
  };

  const handleConfirmDelivery = async (e) => {
    e.preventDefault();
    setActing(true);
    try {
      await transportProviderAPI.confirmDelivery(id, deliveryOtpInput.trim());
      toast.success('تم تأكيد التسليم');
      setDeliveryOtpInput('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'رمز غير صحيح');
    } finally { setActing(false); }
  };

  if (loading) return <div dir="rtl" className="p-6 text-center text-gray-400">جاري التحميل...</div>;
  if (!order) return <div dir="rtl" className="p-6 text-center text-gray-400">الطلب غير موجود</div>;

  const req = order.request || {};

  return (
    <div dir="rtl" className="p-4 sm:p-6 max-w-3xl mx-auto">
      <button onClick={() => navigate('/transport-provider/orders')} className="text-sm text-blue-700 hover:underline mb-4">← رجوع للطلبات</button>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 mb-4">
        <div className="flex items-center justify-between mb-3">
          <h1 className="font-black text-gray-800">{order.orderNumber}</h1>
          <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${STATUS_LABELS[order.status]?.color || 'bg-gray-100 text-gray-600'}`}>
            {STATUS_LABELS[order.status]?.label || order.status}
          </span>
        </div>
        <p className="text-sm text-gray-700 font-semibold mb-1">{req.fromLabel} ← {req.toLabel}</p>
        {req.customer && (
          <div className="flex items-center justify-between bg-blue-50 border border-blue-100 rounded-xl px-3 py-2.5 mb-2">
            <div>
              <p className="text-xs text-blue-600 font-semibold">العميل</p>
              <p className="text-sm font-bold text-gray-800">{req.customer.name}</p>
            </div>
            {req.customer.phone && (
              <a href={`tel:${req.customer.phone}`} className="text-sm font-bold text-blue-700 hover:underline" dir="ltr">
                📞 {req.customer.phone}
              </a>
            )}
          </div>
        )}
        {req.cargoDescription && <p className="text-xs text-gray-500 mb-2">{req.cargoDescription}</p>}
        <div className="flex gap-4 text-xs text-gray-600 mb-3">
          {req.weightKg && <span>⚖️ {req.weightKg} كجم</span>}
          {req.quantity && <span>📦 {req.quantity} قطعة</span>}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 bg-gray-50 rounded-xl p-3">
          <div><p className="text-xs text-gray-500">السعر الإجمالي</p><p className="font-bold text-gray-800">{order.price} ج.س</p></div>
          <div><p className="text-xs text-gray-500">عمولة سافر</p><p className="font-bold text-red-600">{order.commissionAmount} ج.س</p></div>
          <div><p className="text-xs text-gray-500">صافي المستحق</p><p className="font-bold text-emerald-700">{order.providerReceivable} ج.س</p></div>
        </div>
      </div>

      {/* ── Action panels — one per current status ── */}
      {['payment_pending', 'payment_submitted'].includes(order.status) && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 text-sm text-amber-800">
          بانتظار تأكيد الدفع من إدارة سافر — سيتم إشعارك فور التأكيد لتتمكن من تعيين مركبة وسائق.
        </div>
      )}

      {order.status === 'payment_confirmed' && (
        <form onSubmit={handleAssign} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-3">
          <h2 className="font-bold text-gray-800 mb-1">تعيين مركبة وسائق</h2>
          {(() => {
            const usableVehicles = vehicles.filter((v) => v.isActive && v.driverName && v.driverPhone);
            if (usableVehicles.length === 0) {
              return (
                <p className="text-sm text-amber-700 bg-amber-50 rounded-xl p-3">
                  لا توجد مركبة نشطة ببيانات سائق مكتملة. أضف مركبة أو أكمل بيانات السائق من صفحة «أسطولي» أولاً.
                </p>
              );
            }
            return (
              <div>
                <label className={labelCls}>المركبة والسائق *</label>
                <select className={inputCls} value={vehicleId} onChange={(e) => setVehicleId(e.target.value)} required>
                  <option value="">— اختر —</option>
                  {usableVehicles.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.driverName} — {v.driverPhone} — {v.plateNumber || 'بلا لوحة'}
                    </option>
                  ))}
                </select>
              </div>
            );
          })()}
          <button type="submit" disabled={acting || !vehicleId} className="w-full bg-blue-700 hover:bg-blue-800 disabled:opacity-60 text-white py-2.5 rounded-xl font-bold transition">
            {acting ? '...جارٍ الحفظ' : 'تعيين وإرسال رمز الاستلام للعميل'}
          </button>
        </form>
      )}

      {order.status === 'vehicle_assigned' && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <h2 className="font-bold text-gray-800 mb-1">تأكيد استلام البضاعة</h2>
          <p className="text-xs text-gray-500 mb-3">اطلب من العميل رمز الاستلام المرسل له عبر واتساب وأدخله هنا</p>
          <form onSubmit={handleConfirmPickup} className="flex gap-2">
            <input
              className={`${inputCls} font-mono tracking-widest text-center flex-1 min-w-0`} maxLength={6} dir="ltr"
              placeholder="000000" value={pickupOtpInput} onChange={(e) => setPickupOtpInput(e.target.value)}
            />
            <button type="submit" disabled={acting} className="bg-blue-700 hover:bg-blue-800 disabled:opacity-60 text-white px-5 rounded-xl font-bold text-sm transition flex-shrink-0">
              تأكيد
            </button>
          </form>
        </div>
      )}

      {['picked_up', 'in_transit'].includes(order.status) && (
        <div className="space-y-4">
          {order.status === 'picked_up' && (
            <button onClick={handleMarkInTransit} disabled={acting}
              className="w-full bg-purple-600 hover:bg-purple-700 disabled:opacity-60 text-white py-2.5 rounded-xl font-bold text-sm transition">
              تحديث الحالة إلى «في الطريق»
            </button>
          )}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <h2 className="font-bold text-gray-800 mb-1">تأكيد تسليم البضاعة</h2>
            <p className="text-xs text-gray-500 mb-3">اطلب من العميل رمز التسليم وأدخله هنا لإنهاء الطلب</p>
            <form onSubmit={handleConfirmDelivery} className="flex gap-2">
              <input
                className={`${inputCls} font-mono tracking-widest text-center flex-1 min-w-0`} maxLength={6} dir="ltr"
                placeholder="000000" value={deliveryOtpInput} onChange={(e) => setDeliveryOtpInput(e.target.value)}
              />
              <button type="submit" disabled={acting} className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white px-5 rounded-xl font-bold text-sm transition flex-shrink-0">
                تأكيد
              </button>
            </form>
          </div>
        </div>
      )}

      {['delivered', 'completed'].includes(order.status) && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 text-sm text-emerald-800 text-center font-bold">
          ✅ تم تسليم الطلب بنجاح
        </div>
      )}

      {/* ── Timeline ── */}
      {order.events?.length > 0 && (
        <div className="mt-6">
          <p className="text-xs font-bold text-gray-600 mb-2">سجل الطلب</p>
          <div className="space-y-2">
            {order.events.map((ev) => (
              <div key={ev.id} className="flex items-center gap-3 text-xs">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0" />
                <span className="text-gray-700 font-semibold">{EVENT_LABELS[ev.event] || ev.event}</span>
                <span className="text-gray-400">{new Date(ev.createdAt).toLocaleString('ar-SA')}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TransportProviderOrderDetailPage;
